import { Button, Dialog, DialogContent, DialogHeader, DialogTitle } from '@commander/ui';
import type React from 'react';
import { useState } from 'react';

interface TwentyConnectModalProps {
  show: boolean;
  onClose: () => void;
  onConnect: (values: { apiKey: string; crmUrl: string }) => void;
}

export default function TwentyConnectModal({ show, onClose, onConnect }: TwentyConnectModalProps) {
  const [apiKey, setApiKey] = useState('');
  const [crmUrl, setCrmUrl] = useState('');
  const [error, setError] = useState('');

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    // Validation
    if (!apiKey.trim()) {
      setError('Please enter your API Key');
      return;
    }

    if (!crmUrl.trim()) {
      setError('Please enter your Twenty CRM URL');
      return;
    }

    // Clean up: strip trailing slash
    const cleanUrl = crmUrl.trim().replace(/\/+$/, '');

    // Validate URL format (hostname only, no protocol — mask handles that)
    const urlRegex = /^[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    if (!urlRegex.test(cleanUrl)) {
      setError('Please enter a valid Twenty CRM URL (e.g., twenty.yourcompany.com)');
      return;
    }

    // Send full URI — backend requires a valid URI with protocol
    onConnect({ apiKey: apiKey.trim(), crmUrl: `https://${cleanUrl}` });
  };

  const handleClose = () => {
    setApiKey('');
    setCrmUrl('');
    setError('');
    onClose();
  };

  return (
    <Dialog open={show} onOpenChange={handleClose}>
      <DialogContent className="max-w-md z-[99999]">
        <DialogHeader>
          <DialogTitle>Connect Twenty CRM</DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label htmlFor="crmUrl" className="block text-sm font-medium text-gray-700 mb-1">
              Twenty CRM URL
            </label>
            <div className="flex">
              <span className="inline-flex items-center px-3 py-2 border border-r-0 border-gray-300 rounded-l-md bg-gray-50 text-gray-500 text-sm">
                https://
              </span>
              <input
                type="text"
                id="crmUrl"
                value={crmUrl}
                onChange={(e) => {
                  // Strip protocol if user pastes a full URL
                  let val = e.target.value;
                  if (val.startsWith('https://')) val = val.slice(8);
                  else if (val.startsWith('http://')) val = val.slice(7);
                  setCrmUrl(val);
                }}
                placeholder="twenty.yourcompany.com"
                className="w-full px-3 py-2 border border-gray-300 rounded-r-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
              />
            </div>
            <p className="mt-1 text-xs text-gray-500">Enter your Twenty CRM instance URL</p>
          </div>

          <div>
            <label htmlFor="apiKey" className="block text-sm font-medium text-gray-700 mb-1">
              API Key
            </label>
            <input
              type="password"
              id="apiKey"
              value={apiKey}
              onChange={(e) => setApiKey(e.target.value)}
              placeholder="Enter your Twenty CRM API key"
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
            />
            <p className="mt-1 text-xs text-gray-500">
              You can find your API key in your Twenty CRM settings
            </p>
          </div>

          {error && <div className="text-red-600 text-sm">{error}</div>}

          <div className="flex gap-3 justify-end">
            <Button type="button" variant="outline" onClick={handleClose} className="px-4 py-2">
              Cancel
            </Button>
            <Button type="submit" className="px-4 py-2 bg-primary text-white hover:bg-primary/90">
              Connect
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
